<script>
  import * as yup from "yup";
  import MyModal from "../components/MyModal.svelte";
  import RestActions from "../components/RestActions.svelte";
  import { navigate } from "svelte-routing";
  import RestPilots from "../components/RestPilots.svelte";
  import { createEventDispatcher, onMount } from "svelte";
  import { account, appSession, appConstants } from "../stores/app.js";
  import { GetStateList, getStateFromZip } from "../library/util.js";
  import Select from "svelte-select";

  export let inviteCode = "";

  const dispatch = createEventDispatcher();

  let busy = false;
  let myModal;
  let restActions = null;
  let restPilots = null;

  let states = [];
  let stateList = GetStateList();
  for (let code in stateList) {
    states.push({ value: code, label: stateList[code] });
  }

  let stateSelectedValue = null;
  $: {
    if (stateSelectedValue != null) form.data.billing_state = stateSelectedValue.value;
  }

  let form = {
    dirty: false,
    valid: true,
    submitted: false,

    data: {
      invitecode: inviteCode,
      password: "",
      repeatpassword: "",
      billing_city: "",
      billing_state: "",
      billing_country: "United States",
      billing_postcode: "",
      accepted: false,
    },

    schema: yup.object().shape({
      invitecode: yup.string().nullable().required({ message: "Required" }),
      password: yup
        .string()
        .test("password", "8 characters minimum including one number & uppercase letter", (value) => {
          return ValidPassword(value);
        })
        .required({ message: "Required" }),
      repeatpassword: yup
        .string()
        .test("repeatpassword", "Doesn't match", (value) => {
          return value === form.data.password;
        })
        .required({ message: "Required" }),
      billing_state: yup.string().required({ message: "Required" }),
      billing_postcode: yup
        .string()
        .test("billing_postcode", "Invalid for selected state", (value) => {
          let state = getStateFromZip(value);
          if (!state) return false;
          if (state.code != form.data.billing_state) return false;
          else return true;
        })
        .required({ message: "Required" }),
      accepted: yup.bool().test("accepted", "Required", (value) => {
        console.log(value)
        return value;
      }),
    }),
    errors: null,
  };

  onMount(() => {
    M.updateTextFields();
  });

  async function FieldChanged(field) {
    form.dirty = true;
  }

  async function register() {
    form.submitted = true;

    try {
      await ValidateForm(form);
      busy = true;
      let token = await restActions.PilotSetup(
        form.data.invitecode,
        form.data.password,
        form.data.billing_city,
        form.data.billing_state,
        form.data.billing_country,
        form.data.billing_postcode
      );

      let pilotId = parseInt(token.split("-")[3]);

      // set access token
      appSession.set({
        authorisationToken: token,
        pilotId: pilotId,
      });

      // set pilot account details
      let pilot = await restPilots.GetPilots(pilotId);

      account.set({
        name: pilot.lastname.toUpperCase() + ", " + pilot.firstname,
        email: pilot.email,
        subscriptionRequired: true,
        subscriptionExpired: true,
      });

      busy = false;
    } catch (error) {
      busy = false;

      switch (error) {
        case "BadRole":
        case "BadRealm":
          appSession.set({ authorisationToken: null, operatorId: null });
          break;
        case "NetworkError":
          myModal.showAlert(
            "Network error",
            "A network error occurred.<p> Check network connection and try again.</p>"
          );
          break;
        case "RecordNotFound":
          myModal.showAlert(
            "Account not found",
            "Please confirm your invite code is correct and check with your 135 operator."
          );
          break;
        case "BadAuthorisationCode":
          myModal.showAlert("Invalid invite code", "Check that your invite code has been entered correctly.");
          break;
        default:
          myModal.showAlert("Error", error);
          break;
      }
    }
  }

  function ValidateForm() {
    return new Promise(function (resolve, reject) {
      form.schema
        .validate(form.data, { abortEarly: false })
        .then((values) => {
          form.valid = true;
          form.errors = {};

          resolve(true);
        })
        .catch((validationErrors) => {
          form.valid = false;
          form.errors = {};

          for (let i = 0; i < validationErrors.inner.length; i++) {
            let error = validationErrors.inner[i];
            form.errors[error.path] = {
              type: error.type,
              message: typeof error.errors[0] == "object" ? error.errors[0].message : error.errors[0],
            };
          }
        });
    });
  }

  function handleKeydown(event) {
    if (event.key == "Enter") register();
  }

  function ValidPassword(password) {
    if (password.length < 8) return false;

    let oneNumeric = /[0-9]/g;
    if (!oneNumeric.test(password)) return false;

    let oneUppercase = /[A-Z]/g;
    if (!oneUppercase.test(password)) return false;

    return true;
  }
</script>

<div class="container">

  <div class="row">
    <div class="col s12 m8 offset-m2 l6 offset-l3">

      <img
        src="assets/logo.png"
        style="display: block; max-width: 80%; height: auto; margin: 40px auto 40px auto;"
        alt="" />

      <h5 style="text-align: center;">Pilot Registration</h5>

      <p>Please supply your invite code and complete the form below.</p>

      <div class="input-field" style="margin-top: 40px;">
        <input type="text" bind:value={form.data.invitecode} />
        <label>Invite code</label>
        {#if form.errors && form.errors.invitecode}
          <div class="helper-text red-text">{form.errors.invitecode.message}</div>
        {/if}
      </div>

      <div
        style="font-weight: 500; color: #000; font-size: 0.85em; margin-top: 30px; margin-bottom: 15px; text-transform:
        uppercase;">
        Contact Details
      </div>

      <div class="input-field">
        <input
          type="text"
          autocomplete="off"
          bind:value={form.data.billing_city}
          on:change={() => FieldChanged('billing_city')} />
        <label>City</label>
        {#if form.errors && form.errors.billing_city}
          <div class="helper-text red-text">{form.errors.billing_city.message}</div>
        {/if}
      </div>

      <label class="active" style="display: block; margin-bottom: 10px;">State</label>
      <div class="svelte-select">
        <Select
          items={states}
          bind:selectedValue={stateSelectedValue}
          isSearchable={true}
          placeholder="Select..."
          isClearable={true}
          showChevron={true}
          on:select={() => {
            FieldChanged('billing_state');
          }}
          on:clear={() => {
            FieldChanged('billing_state');
            stateSelectedValue = null;
            form.data.billing_state = '';
          }} />
      </div>
      <div class="input-field" style="margin-top: 0.5em;">
        {#if form.errors && form.errors.billing_state}
          <span class="helper-text red-text">{form.errors.billing_state.message}</span>
        {/if}
      </div>

      <div class="input-field" style="margin-top: 30px;">
        <input
          type="text"
          autocomplete="off"
          bind:value={form.data.billing_country}
          on:change={() => FieldChanged('billing_country')}
          disabled />
        <label>Country</label>
        {#if form.errors && form.errors.billing_country}
          <div class="helper-text red-text">{form.errors.billing_country.message}</div>
        {/if}
      </div>

      <div class="input-field">
        <input
          type="text"
          autocomplete="off"
          bind:value={form.data.billing_postcode}
          on:change={() => FieldChanged('billing_postcode')}
          maxlength="5" />
        <label>Zip/Post Code</label>
        {#if form.errors && form.errors.billing_postcode}
          <div class="helper-text red-text">{form.errors.billing_postcode.message}</div>
        {/if}
      </div>

      <div
        style="font-weight: 500; color: #000; font-size: 0.85em; margin-top: 30px; margin-bottom: 15px; text-transform:
        uppercase;">
        Security
      </div>

      <div class="input-field">
        <input type="password" bind:value={form.data.password} />
        <label>Password</label>
        {#if form.errors && form.errors.password}
          <div class="helper-text red-text">{form.errors.password.message}</div>
        {/if}
      </div>

      <div class="input-field">
        <input type="password" bind:value={form.data.repeatpassword} />
        <label>Repeat password</label>
        {#if form.errors && form.errors.repeatpassword}
          <div class="helper-text red-text">{form.errors.repeatpassword.message}</div>
        {/if}
      </div>

      <div
        style="font-weight: 500; color: #000; font-size: 0.85em; margin-top: 30px; margin-bottom: 15px; text-transform:
        uppercase;">
        Terms & conditions
      </div>


        <label>
          <input type="checkbox" bind:checked={form.data.accepted} />
          <span>
            I have read and accept the Jumphub
            <a href="https://www.jumphub.aero/terms-and-conditions" target=_blank>Terms&nbsp;&&nbsp;Conditions</a>
          </span>
        </label>
        <div class="input-field">
        {#if form.errors && form.errors.accepted}
          <div class="helper-text red-text">{form.errors.accepted.message}</div>
        {/if}
      </div>

      <div style="margin-top: 40px; margin-bottom: 40px;">
        <button
          class="btn right blue-grey wave-effect"
          on:click={() => {
            register();
          }}
          disabled={busy}>
          Continue
        </button>
      </div>

    </div>
  </div>

</div>

<MyModal bind:this={myModal} />
<RestActions bind:this={restActions} />
<RestPilots bind:this={restPilots} />

<svelte:window on:keydown={handleKeydown} />
